import React, { useEffect, useState } from "react";
import { Snackbar, Alert } from "@mui/material";
import confetti from "canvas-confetti";
import { getCurrentUser } from "../services/pocketbase";

const RayTracingMode = () => {
  const [active, setActive] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [roleAssigned, setRoleAssigned] = useState(false);
  const [roleError, setRoleError] = useState(null);
  const user = getCurrentUser();

  useEffect(() => {
    // Handler for keyboard shortcut (Ctrl+Alt+F)
    const handleKeyDown = (event) => {
      if (event.ctrlKey && event.altKey && event.key === "f") {
        event.preventDefault(); // Prevent browser's find function
        setActive((prev) => !prev);
        setShowTooltip(true);

        // Only attempt to assign role if we have a Discord ID and role hasn't been assigned yet
        if (user?.discord_id && !roleAssigned) {
          assignDiscordRole();
        }

        // Show tooltip for 3 seconds
        setTimeout(() => setShowTooltip(false), 3000);
      }
    };

    // Add event listener
    window.addEventListener("keydown", handleKeyDown);

    // Apply or remove effects when active state changes
    if (active) {
      applyRayTracingEffects();
    } else {
      removeRayTracingEffects();
    }

    // Clean up
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      removeRayTracingEffects();
    };
    // eslint-disable-next-line
  }, [active, roleAssigned, user]);

  // Function to assign Discord role
  const assignDiscordRole = async () => {
    try {
      setRoleError(null);
      
      const response = await fetch("/api/discord/assign-role", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          discordId: user.discord_id,
          roleId: "1374794370141917225", // RTX Mode role ID
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.details || data.error || "Failed to assign role");
      }

      setRoleAssigned(true);
    } catch (err) {
      console.error("Error assigning RTX role:", err);
      setRoleError(err.message || "Failed to assign Discord role");
    }
  };

  // Apply the fake RTX effects
  const applyRayTracingEffects = () => {
    // 1. Add reflections to buttons
    const buttons = document.querySelectorAll("button, a.MuiButtonBase-root");
    buttons.forEach((button) => {
      button.classList.add("rtx-reflection");

      // Create a reflection overlay for each button
      const reflection = document.createElement("div");
      reflection.className = "rtx-reflection-overlay";
      button.appendChild(reflection);
    });

    // 2. Add CSS for reflections
    const style = document.createElement("style");
    style.id = "rtx-style";
    style.innerHTML = `
      .rtx-reflection {
        position: relative;
        overflow: hidden !important;
        z-index: 1;
      }
      
      .rtx-reflection::before {
        content: "";
        position: absolute;
        top: -50%;
        left: -50%;
        width: 200%;
        height: 200%;
        background: linear-gradient(
          45deg,
          rgba(255, 255, 255, 0) 0%,
          rgba(255, 255, 255, 0.1) 50%,
          rgba(255, 255, 255, 0) 100%
        );
        transform: rotate(45deg);
        animation: rtx-sweep 3s infinite linear;
        z-index: -1;
      }
      
      .rtx-reflection-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(
          to bottom,
          rgba(255, 255, 255, 0.2) 0%,
          rgba(255, 255, 255, 0) 50%
        );
        pointer-events: none;
        z-index: 2;
      }
      
      @keyframes rtx-sweep {
        0% {
          transform: rotate(45deg) translateY(-100%);
        }
        100% {
          transform: rotate(45deg) translateY(100%);
        }
      }
      
      /* Add bloom effect to the entire page */
      body.rtx-active {
        filter: brightness(1.1) saturate(1.2);
      }
    `;
    document.head.appendChild(style);
    document.body.classList.add("rtx-active");

    // 3. Start particle effects (using canvas-confetti)
    startParticleEffects();
  };

  // Remove all RTX effects
  const removeRayTracingEffects = () => {
    // Remove reflections from buttons
    const buttons = document.querySelectorAll("button, a.MuiButtonBase-root");
    buttons.forEach((button) => {
      button.classList.remove("rtx-reflection");
      const reflection = button.querySelector(".rtx-reflection-overlay");
      if (reflection) {
        button.removeChild(reflection);
      }
    });

    // Remove CSS
    const style = document.getElementById("rtx-style");
    if (style) {
      document.head.removeChild(style);
    }
    document.body.classList.remove("rtx-active");

    // Stop particle interval if it exists
    if (window.rtxParticleInterval) {
      clearInterval(window.rtxParticleInterval);
      window.rtxParticleInterval = null;
    }
  };

  // Start ridiculous particle effects
  const startParticleEffects = () => {
    // Clear any existing interval
    if (window.rtxParticleInterval) {
      clearInterval(window.rtxParticleInterval);
    }

    // Create random particles at intervals
    window.rtxParticleInterval = setInterval(() => {
      // Random position
      const x = Math.random();
      const y = Math.random() * 0.5;

      // Create a burst of particles
      confetti({
        particleCount: 15,
        spread: 40,
        origin: { x, y },
        colors: ["#c4d4e0", "#ffffff", "#00ffff", "#ff00ff"],
        gravity: 0.3,
        scalar: 0.7,
        drift: 0.1,
        ticks: 100,
      });
    }, 800);
  };

  return (
    <Snackbar
      open={showTooltip}
      autoHideDuration={3000}
      onClose={() => setShowTooltip(false)}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
    >
      <Alert
        severity={active ? "warning" : "info"}
        sx={{
          width: "100%",
          background: active
            ? "linear-gradient(45deg, #ff8a00, #e52e71)"
            : undefined,
          color: active ? "white" : undefined,
          fontWeight: active ? "bold" : undefined,
          boxShadow: active ? "0 0 15px rgba(255, 138, 0, 0.7)" : undefined,
        }}
      >
        {active
          ? roleAssigned 
            ? "RTX ON! Your GPU is crying 😭 (RTX Discord role assigned!)"
            : roleError 
              ? `RTX ON! Your GPU is crying 😭 (Role error: ${roleError})`
              : user?.discord_id 
                ? "RTX ON! Your GPU is crying 😭 (Assigning Discord role...)" 
                : "RTX ON! Your GPU is crying 😭 (Link Discord to get the role!)"
          : "RTX OFF! Your GPU thanks you 🙏"}
      </Alert>
    </Snackbar>
  );
};

export default RayTracingMode;
